import { useWizard } from "@picklebet/ui";
import { convertAmountToCents } from "common/currency";
import { walletService } from "@/features/wallet/services/wallet";
import { err } from "utilities/result";
import { useInteracAddresses } from "@/features/wallet/hooks/interac/useInteracAddresses";
import { pickInteracWithdrawalAddress } from "@/features/wallet/utils/interac";
import { useWithdrawWizard } from "./useWithdrawWizard";
import type { WithdrawStepName } from "../utils/registry";
import type { InteracAddress } from "hooks/firestore/models/interac/interacAddress";
import { useMemo } from "react";

const sortInteracAddresses = (addresses: InteracAddress[]) => {
  return Array.from(addresses).sort((a, b) => {
    return (
      Number(!!b.isDefaultWithdrawalWallet) -
      Number(!!a.isDefaultWithdrawalWallet)
    );
  });
};

export const useInteracWithdrawal = () => {
  const { goTo } = useWizard<WithdrawStepName>();
  const { interacWalletId } = useWithdrawWizard();
  const { interacAddresses, isLoading, error } = useInteracAddresses();

  const sortedInteracAddresses = useMemo(() => {
    if (!interacAddresses) return [];
    return sortInteracAddresses(interacAddresses);
  }, [interacAddresses]);

  const selectedAddress = pickInteracWithdrawalAddress(
    sortedInteracAddresses,
    interacWalletId,
  );

  const submit = async (amount: number) => {
    if (!selectedAddress) {
      return err(new Error("Please add Interac details to withdraw to"));
    }

    const result = await walletService.withdrawCash({
      amount: convertAmountToCents(amount),
      currency: "CAD",
      externalType: "GIGADAT_INTERAC",
      userExternalWalletId: selectedAddress.userExternalWalletId,
    });

    if (result.ok) {
      goTo("withdraw-success", { replace: true });
    }

    return result;
  };

  return {
    submit,
    selectedAddress,
    interacAddresses: sortedInteracAddresses,
    isLoading,
    error,
  };
};
